import { numberFormat } from '@libs/core';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import parse, { attributesToProps, domToReact, HTMLReactParserOptions } from 'html-react-parser';
import React, { useMemo } from 'react';
import { TTextConsumerData, TTextTransformation } from '../../../types/data';
import styles from './TextCommon.module.css';

// Props definition remains the same
interface HighlighterProps {
    content: string;
    textTransformation?: TTextConsumerData['textTransformation'];
    onClickAction?: (actionName: string) => void;
}

const DATA_ACTION_NAME = 'data-action-name';

const highlightText = (content: string, transform: TTextTransformation) => {
    // 1. Replace all newline characters with <br /> tags.
    const textWithBreaks = content.replace(/\n/g, '<br />').trim();
    const highlight = transform.text;

    if (!highlight) {
        return textWithBreaks;
    }

    // 2. Build a "clean" text version (where tags are replaced by a single space)
    //    and a map to correlate indices from the clean text back to the original.
    //    This fixes issues where matches occurred after HTML tags.
    const map: number[] = [];
    let cleanText = '';
    let i = 0;
    while (i < textWithBreaks.length) {
        if (textWithBreaks[i] === '<') {
            const tagEnd = textWithBreaks.indexOf('>', i);
            if (tagEnd === -1) {
                // Unclosed tag, treat as text
                cleanText += textWithBreaks[i];
                map.push(i);
                i++;
            } else {
                // It's a tag. Replace with a single space in cleanText.
                if (cleanText.length === 0 || cleanText[cleanText.length - 1] !== ' ') {
                    cleanText += ' ';
                    // Map the space to the start of the tag.
                    map.push(i);
                }
                i = tagEnd + 1;
            }
        } else {
            cleanText += textWithBreaks[i];
            map.push(i);
            i++;
        }
    }
    // Add the final length for easy slicing of the last match.
    map.push(textWithBreaks.length);

    // 3. Find all matches of the highlight term in the clean text.
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const searchRegex = new RegExp(escapedHighlight.replace(/\s+/g, '\\s+'), 'gi');
    let matches = [...cleanText.matchAll(searchRegex)];

    if (matches.length === 0) {
        return textWithBreaks;
    } else if (!transform.isRepeatable) {
        matches = [matches[0]];
    }

    // 4. Iterate backwards through matches and wrap them with <span> tags.
    let resultString = textWithBreaks;
    for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];
        if (match.index === undefined) continue;

        const matchEnd = match.index + match[0].length;

        // Use the map to find the corresponding start and end indices in the original string.
        const originalStart = map[match.index];
        const originalEnd = map[matchEnd];

        if (originalStart !== undefined && originalEnd !== undefined) {
            const styleParts: string[] = [];
            if (transform.textColor) {
                styleParts.push(`color: var(--${transform.textColor})`);
            }

            if (styleParts.length > 0 || transform.smallCaps || transform.useLocaleNumberFormat || transform.link || transform.actionName) {
                const originalSlice = resultString.slice(originalStart, originalEnd);
                const textToRender = transform.useLocaleNumberFormat ? (numberFormat(originalSlice, transform.precision) as string) + ' ' : originalSlice;

                const style = styleParts.join('; ');
                const finalClassName = clsx(transform.smallCaps && styles.smallCaps, transform.actionName && styles.actionable);
                const actionNameAttr = transform.actionName ? `data-action-name="${transform.actionName}"` : '';
                if (transform.link) {
                    resultString =
                        resultString.slice(0, originalStart) +
                        `<a href="${transform.link}" target="_blank" rel="noopener noreferrer" class="${finalClassName}" style="${style}">${textToRender}</a>` +
                        resultString.slice(originalEnd);
                } else {
                    const haveTransformation = style || finalClassName;
                    resultString = haveTransformation
                        ? resultString.slice(0, originalStart) +
                          `<span class="${finalClassName}" style="${style}" ${actionNameAttr}>${textToRender}</span>` +
                          resultString.slice(originalEnd)
                        : resultString.slice(0, originalStart) + textToRender + resultString.slice(originalEnd);
                }
            }
        }
    }

    return resultString;
};

/**
 * A React component that correctly highlights search terms, handles HTML,
 * and converts newlines to <br /> tags.
 */
export const Highlighter: React.FC<HighlighterProps> = ({ content, textTransformation, onClickAction }) => {
    'use no memo';
    return useMemo(() => {
        let transformedText = highlightText(content, {});

        if (Array.isArray(textTransformation) && textTransformation.length > 0) {
            textTransformation
                .filter((t) => t?.text && (t?.textColor || t?.smallCaps || t?.useLocaleNumberFormat || t?.link || t?.actionName))
                .forEach((transform) => {
                    transformedText = highlightText(transformedText, transform);
                });
        }
        const options: HTMLReactParserOptions = {
            trim: false,
            replace: (domNode) => {
                //@ts-expect-error
                if (domNode?.attribs) {
                    //@ts-expect-error
                    const props = attributesToProps(domNode.attribs);
                    //@ts-expect-error
                    const children = domToReact(domNode.children, options);

                    //@ts-expect-error
                    if (domNode.name === 'a') {
                        return <a {...props}>{children}</a>;
                    }
                    //@ts-expect-error
                    if (domNode.name === 'span') {
                        if (props[DATA_ACTION_NAME] && onClickAction) {
                            return (
                                <span
                                    {...props}
                                    onClick={() => {
                                        //@ts-expect-error
                                        onClickAction(props[DATA_ACTION_NAME]);
                                    }}
                                >
                                    {children}
                                </span>
                            );
                        }
                        return <span {...props}>{children}</span>;
                    }
                }
            },
        };

        const sanitizedText = DOMPurify.sanitize(transformedText, {
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', DATA_ACTION_NAME],
            ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tg|svg|tel|callto|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
        });

        return parse(sanitizedText, options);
    }, [content, textTransformation, onClickAction]);
};
